import React, { useState, useCallback } from 'react';
import { Modal, Table, Button, Tooltip, Grid, Descriptions } from 'antd';
import useSwr from '@hooks/useSwr';
import { hourFormat, dateFormat, bytesFormat } from '@utils/formatters';
import { useTranslation } from 'react-i18next';
import { FileSearchOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

interface IProps {
  customerId: number | undefined;
  objectId: string;
}

const AccountsModal: React.FC<IProps> = ({ customerId, objectId }) => {
  const { t } = useTranslation('sessions');
  const [page, setPage] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const screens = useBreakpoint();

  const { data: accountsData } = useSwr(
    isModalVisible ? `service-proxy/accts/by-day/sessions` : null,
    {
      params: {
        customerId,
        objectId,
      },
    },
  );

  const showModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const columns = [
    {
      title: t('table.ip'),
      dataIndex: 'ip',
    },
    {
      title: t('table.technology'),
      dataIndex: 'technology',
    },
    {
      title: t('table.operator'),
      dataIndex: 'operator',
    },
    {
      title: t('table.duration'),
      dataIndex: 'sessiontime',
      render: (value: any) => value && hourFormat(value),
    },
    {
      title: t('table.consume'),
      dataIndex: 'outputbytes',
      align: 'center' as const,
      render: (value: any, record: any) => {
        const sum = (value ?? 0) + (record?.inputbytes ?? 0);
        const result = bytesFormat(sum);

        return result;
      },
    },
    {
      title: t('table.acctStartTime'),
      dataIndex: 'starttime',
      render: (value: any) => value && dateFormat(value),
    },
    {
      title: t('table.acctStopTime'),
      dataIndex: 'stoptime',
      render: (value: any) => value && dateFormat(value),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setPage(pagination.current - 1);
  };

  return (
    <>
      <Tooltip title={t('details')}>
        <Button type="link" onClick={showModal}>
          <FileSearchOutlined />
        </Button>
      </Tooltip>

      <Modal
        title={t('accounts-details')}
        width={screens?.xs ? '90%' : '60%'}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Descriptions
          column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
          style={{ padding: 24 }}
        >
          <Descriptions.Item label={t('table.imsi')}>
            {accountsData?.imsi ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('table.msisdn')}>
            {accountsData?.msisdn ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('table.imei')}>
            {accountsData?.imei ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('table.apn')}>
            {accountsData?.apn ?? '-'}
          </Descriptions.Item>
        </Descriptions>
        <div>
          <Table
            columns={columns}
            dataSource={accountsData?.sessions}
            loading={!accountsData?.sessions}
            scroll={{
              x: 'max-content',
            }}
            pagination={{
              current: page + 1,
              pageSize: 20,
              total: accountsData?.sessions?.length || 0,
            }}
            onChange={handleTableChange}
          />
        </div>
      </Modal>
    </>
  );
};

export default AccountsModal;
